import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import './AdminDashboardView.css';
import DevManagement from './DevManagement';

// Import components for event management
import AdminPlaceholderUpload from './AdminPlaceholderUpload';

const AdminDashboardView = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    users: 0,
    events: 0,
    templates: 0,
    uploads: 0
  });
  const [mockUsers, setMockUsers] = useState([]);
  const [userToEdit, setUserToEdit] = useState(null);
  const [formValues, setFormValues] = useState({
    first_name: '',
    last_name: '',
    email: '',
    user_type: 'customer',
    is_admin: false
  });
  const [showUserForm, setShowUserForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const containerRef = useRef(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        // Always set isAdmin to true for development/testing purposes
        // This bypasses the actual admin check in Supabase
        setIsAdmin(true);
        
        // Initialize mock data
        initMockData();
        
        // Fetch real data
        fetchEvents();
        fetchUsers();
        
        setLoading(false);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  // Add this useEffect that will run whenever activeSection changes
  useEffect(() => {
    // Reset scroll position when changing sections
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
      
      // Force a reflow to ensure proper scroll behavior
      setTimeout(() => {
        // Enable scrolling by setting overflow properties directly
        containerRef.current.style.overflow = 'auto';
        containerRef.current.style.webkitOverflowScrolling = 'touch';
        containerRef.current.style.touchAction = 'pan-y';
        
        // Add a small amount of content to ensure scrolling is possible
        const sections = containerRef.current.querySelectorAll('.admin-section');
        if (sections.length > 0) {
          sections.forEach(section => {
            section.style.overflow = 'visible';
            section.style.touchAction = 'pan-y';
          });
        }
      }, 100);
    }
  }, [activeSection]);

  useEffect(() => {
    // Function to fix scrolling on iOS devices
    const fixIOSScrolling = () => {
      const container = containerRef.current;
      if (!container) return;
      
      // Add class to help with iOS detection
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        container.classList.add('ios-device');
      }
      
      // Directly add styles for better touch scrolling
      container.style.overflow = 'auto';
      container.style.webkitOverflowScrolling = 'touch';
      container.style.touchAction = 'pan-y';
      
      // Simpler approach to handle scrolling - prevent only in specific cases
      const handleTouchStart = (e) => {
        // Store the initial touch position
        container.dataset.touchStartY = e.touches[0].clientY;
      };
      
      const handleTouchMove = (e) => {
        if (!container.dataset.touchStartY) return;
        
        const touchY = e.touches[0].clientY;
        const startY = parseFloat(container.dataset.touchStartY);
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const height = container.clientHeight;
        
        // Prevent default only if scrolling would be blocked at boundaries
        const isScrollingUp = touchY > startY;
        const isScrollingDown = touchY < startY;
        const isAtTop = scrollTop <= 0;
        const isAtBottom = scrollTop + height >= scrollHeight;
        
        if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
          e.preventDefault();
        }
      };
      
      const handleTouchEnd = () => {
        delete container.dataset.touchStartY;
      };
      
      // Clean up any existing listeners first
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      
      // Add the event listeners
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleTouchEnd, { passive: true });
      
      return () => {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      };
    };
    
    const cleanup = fixIOSScrolling();
    return cleanup;
  }, []);

  const fetchEvents = async () => {
    try {
      // Fetch events from Supabase
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching events:', error);
        // Still show mock data as fallback
        useMockEventsData();
        return;
      }
      
      if (data && data.length > 0) {
        console.log('Successfully fetched events from database:', data);
        setEvents(data);
        setStats(prev => ({
          ...prev,
          events: data.length
        }));
      } else {
        console.log('No events found in database, using mock data');
        useMockEventsData();
      }
    } catch (error) {
      console.error('Error in fetchEvents:', error);
      useMockEventsData();
    }
  };
  
  const fetchUsers = async () => {
    try {
      console.log("Attempting to fetch users...");
      
      // Get users from the database
      const { data, error } = await supabase
        .from('app_users')
        .select('*');
      
      console.log("Supabase response:", { data, error });
      
      if (error) {
        console.error('Error fetching users:', error);
        console.log('Falling back to mock users data');
        return; // Mock data is already initialized in initMockData
      }
      
      if (data && data.length > 0) {
        console.log('Successfully fetched users from database:', data);
        setMockUsers(data);
        setStats(prev => ({
          ...prev,
          users: data.length
        }));
      } else {
        console.log('No users found in database, using mock data');
        // Mock data already initialized in initMockData
      }
    } catch (error) {
      console.error('Error in fetchUsers:', error);
      console.log('Using mock user data due to error');
    }
  };

  // Helper function to use mock event data as fallback
  const useMockEventsData = () => {
    const mockEvents = [
      {
        id: 1,
        name: 'Football Championship',
        start_date: '2023-05-15',
        end_date: '2023-05-17',
        event_type: 'sports',
        city: 'Denver',
        state: 'CO',
        status: 'active'
      },
      {
        id: 2,
        name: 'Basketball Tournament',
        start_date: '2023-06-10',
        end_date: '2023-06-12',
        event_type: 'sports',
        city: 'Chicago',
        state: 'IL',
        status: 'active'
      },
      {
        id: 3,
        name: 'Soccer Finals',
        start_date: '2023-07-20',
        end_date: '2023-07-22',
        event_type: 'sports',
        city: 'Dallas',
        state: 'TX',
        status: 'upcoming'
      },
      {
        id: 4,
        name: 'Baseball Series',
        start_date: '2023-04-05',
        end_date: '2023-04-07',
        event_type: 'sports',
        city: 'Boston',
        state: 'MA',
        status: 'completed'
      },
      {
        id: 5,
        name: 'Hockey Championship',
        start_date: '2023-08-15',
        end_date: '2023-08-17',
        event_type: 'sports',
        city: 'Detroit',
        state: 'MI',
        status: 'upcoming'
      }
    ];
    
    setEvents(mockEvents);
    
    // Update stats with event count
    setStats(prev => ({
      ...prev,
      events: mockEvents.length
    }));
  };

  const initMockData = () => {
    // Generate mock users
    const users = [
      {
        id: 'mock-1',
        first_name: 'John',
        last_name: 'Doe',
        email: 'john.doe@example.com',
        username: 'johndoe',
        user_type: 'customer',
        is_admin: false,
        created_at: '2023-04-01T00:00:00.000Z'
      },
      {
        id: 'mock-2',
        first_name: 'Jane',
        last_name: 'Smith',
        email: 'jane.smith@example.com',
        username: 'janesmith',
        user_type: 'staff',
        is_admin: true,
        created_at: '2023-04-02T00:00:00.000Z'
      },
      {
        id: 'mock-3',
        first_name: 'Michael',
        last_name: 'Johnson',
        email: 'michael.johnson@example.com',
        username: 'mjohnson',
        user_type: 'customer',
        is_admin: false,
        created_at: '2023-04-03T00:00:00.000Z'
      }
    ];
    
    setMockUsers(users);
    
    // Set mock stats
    setStats({
      users: users.length,
      events: 5,
      templates: 12,
      uploads: 28
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    
    try {
      // Validate required fields
      if (!formValues.email || !formValues.first_name || !formValues.last_name) {
        // Show error message
        alert("Please fill all required fields");
        setLoading(false);
        return;
      }

      if (userToEdit) {
        // Update existing user
        
        // First update the UI with mock data
        const updatedUsers = mockUsers.map(user => 
          user.id === userToEdit.id 
            ? { ...user, ...formValues }
            : user
        );
        setMockUsers(updatedUsers);
        
        // Then try to update the real database
        try {
          // Update user in Supabase
          const { error } = await supabase
            .from('app_users')
            .update({
              first_name: formValues.first_name,
              last_name: formValues.last_name,
              email: formValues.email,
              user_type: formValues.user_type,
              is_admin: formValues.is_admin,
              updated_at: new Date().toISOString()
            })
            .eq('id', userToEdit.id);
            
          if (error) {
            console.warn("Supabase update error:", error);
            // Even if there's an error, we keep the mock UI updated
          } else {
            console.log("User updated successfully in Supabase");
            setSuccessMessage(`${formValues.first_name} ${formValues.last_name} updated successfully!`);
            
            // Close the form after a short delay
            setTimeout(() => {
              setSuccessMessage('');
              setShowUserForm(false);
            }, 1500);
          }
        } catch (err) {
          console.error("Error updating user in Supabase:", err);
        }
        
      } else {
        // Create new user

        // Generate a UUID for the user
        const newUserId = crypto.randomUUID();
        
        // Create user object for mock data
        const newUser = {
          id: newUserId,
          ...formValues,
          created_at: new Date().toISOString()
        };
        
        // Update the UI with mock data first for immediate feedback
        setMockUsers([...mockUsers, newUser]);
        setStats(prev => ({ ...prev, users: prev.users + 1 }));

        // Then try to create the user in Supabase
        try {
          // APPROACH 1: Just create an entry in the app_users table
          // This approach doesn't create an actual authenticated user
          // but allows us to see the user in the admin interface
          const { data, error } = await supabase
            .from('app_users')
            .insert({
              id: newUserId,
              first_name: formValues.first_name,
              last_name: formValues.last_name,
              email: formValues.email,
              user_type: formValues.user_type,
              is_admin: formValues.is_admin,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

          if (error) {
            console.warn("Error creating user in app_users table:", error);
          } else {
            console.log("User created successfully in app_users table");
            setSuccessMessage(`${formValues.first_name} ${formValues.last_name} created successfully!`);
            
            // Close the form after a short delay
            setTimeout(() => {
              setSuccessMessage('');
              setShowUserForm(false);
            }, 1500);
          }

        } catch (err) {
          console.error("Error creating user in Supabase:", err);
          // Even if there's an error with Supabase, we keep the UI updated for testing
        }
      }
      
      // Reset form only if there's no success message (otherwise we'll reset before displaying it)
      if (!successMessage) {
        setFormValues({
          first_name: '',
          last_name: '',
          email: '',
          user_type: 'customer',
          is_admin: false
        });
        setUserToEdit(null);
        // Don't close the form yet if we have a success message
        if (!successMessage) {
          setShowUserForm(false);
        }
      }
      
    } catch (error) {
      console.error("Form submission error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle events in a more mobile-friendly way
  const handleEventClick = (eventId, e) => {
    // Stop any propagation
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    navigate(`/dashboard/events/${eventId}`);
  };

  // Handle event editing with better touch support
  const handleEditEvent = (eventId, e) => {
    // Stop any propagation
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    window.location.href = `/dashboard/events/${eventId}/edit`;
  };

  // Handle event deletion with better touch support
  const handleDeleteEvent = async (eventId, e) => {
    // Stop any propagation
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    if (!confirm("Are you sure you want to delete this event?")) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Remove from UI first for immediate feedback
      setEvents(currentEvents => currentEvents.filter(event => event.id !== eventId));
      setStats(prev => ({ ...prev, events: prev.events - 1 }));
      
      // Then delete from database
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);
        
      if (error) {
        console.error("Error deleting event:", error);
        alert(`Error deleting event: ${error.message}`);
        
        // Refresh events to restore data if there was an error
        fetchEvents();
      } else {
        alert("Event deleted successfully");
      }
    } catch (error) {
      console.error("Error handling event deletion:", error);
      fetchEvents(); // Refresh to restore data
    } finally {
      setLoading(false);
    }
  };

  // Handle user editing with better touch support
  const handleEditUser = (user, e) => {
    // Stop any propagation
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    setUserToEdit(user);
    setFormValues({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      user_type: user.user_type,
      is_admin: user.is_admin
    });
    setShowUserForm(true);
  };

  // Handle user deletion with better touch support
  const handleDeleteUser = async (userId, e) => {
    // Stop any propagation
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    if (!confirm("Are you sure you want to delete this user?")) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Remove from UI first for immediate feedback
      setMockUsers(currentUsers => currentUsers.filter(user => user.id !== userId));
      setStats(prev => ({ ...prev, users: prev.users - 1 }));
      
      // Then delete from database
      const { error } = await supabase
        .from('app_users')
        .delete()
        .eq('id', userId);
        
      if (error) {
        console.error("Error deleting user:", error);
        alert(`Error deleting user: ${error.message}`);
        
        // Refresh users to restore data if there was an error
        fetchUsers();
      } else {
        alert("User deleted successfully");
      }
    } catch (error) {
      console.error("Error handling user deletion:", error);
      fetchUsers(); // Refresh to restore data
    } finally {
      setLoading(false);
    }
  };

  // Modify the setActiveSection function to also reset scroll behavior
  const handleSectionChange = (section) => {
    setActiveSection(section);
    // Update URL without navigation
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('section', section);
    window.history.pushState({}, '', newUrl);
  };

  useEffect(() => {
    // Function to handle viewport meta tag for mobile
    const setupMobileViewport = () => {
      // Look for existing viewport meta tag
      let viewportMeta = document.querySelector('meta[name="viewport"]');
      
      // If it doesn't exist, create it
      if (!viewportMeta) {
        viewportMeta = document.createElement('meta');
        viewportMeta.name = 'viewport';
        document.head.appendChild(viewportMeta);
      }
      
      // Set the content to prevent unwanted zooming
      const originalContent = viewportMeta.content;
      viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      
      // Return a cleanup function to restore the original meta
      return () => {
        if (originalContent) {
          viewportMeta.content = originalContent;
        } else {
          viewportMeta.remove();
        }
      };
    };
    
    const cleanupViewport = setupMobileViewport();
    
    return () => {
      cleanupViewport();
    };
  }, []);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="not-admin">
        <h2>Access Denied</h2>
        <p>You do not have administrator privileges.</p>
        <Link to="/make-admin" className="admin-link">
          Request Admin Access
        </Link>
      </div>
    );
  }

  // Render the Events Management section
  const renderEventsSection = () => {
    return (
      <div className="admin-section">
        <div className="section-header">
          <h3>Event Management</h3>
          <div className="header-actions">
            <button 
              className="refresh-button"
              onClick={(e) => { e.preventDefault(); fetchEvents(); }}
              title="Refresh Events"
            >
              <i className="fas fa-sync-alt"></i>
            </button>
            <Link 
              to="/dashboard/events/new" 
              className="add-button"
              onClick={(e) => e.stopPropagation()}
            >
              <i className="fas fa-plus"></i> Create Event
            </Link>
          </div>
        </div>
        
        {events.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <i className="fas fa-calendar-alt"></i>
            </div>
            <h3>No Events Available</h3>
            <p>You can create your first event now</p>
            <Link 
              to="/dashboard/events/new" 
              className="add-button"
              onClick={(e) => e.stopPropagation()}
            >
              Create First Event
            </Link>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Dates</th>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => (
                  <tr 
                    key={event.id} 
                    onClick={(e) => handleEventClick(event.id, e)}
                    className="clickable-row"
                    title="Click to view event dashboard"
                  >
                    <td>{event.name}</td>
                    <td>
                      {formatDate(event.start_date)}
                      {event.end_date && event.start_date !== event.end_date && 
                        ` - ${formatDate(event.end_date)}`}
                    </td>
                    <td>{`${event.city}, ${event.state}`}</td>
                    <td>
                      <span className={`event-type ${event.event_type || 'default'}`}>
                        {capitalizeFirstLetter(event.event_type) || 'Default'}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-status ${event.status || 'active'}`}>
                        {capitalizeFirstLetter(event.status) || 'Active'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="action-button view"
                          onClick={(e) => handleEventClick(event.id, e)}
                          title="View Event Dashboard"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button 
                          className="action-button edit"
                          onClick={(e) => handleEditEvent(event.id, e)}
                          title="Edit Event"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          className="action-button delete"
                          onClick={(e) => handleDeleteEvent(event.id, e)}
                          title="Delete Event"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };
  
  // Helper function to format dates nicely
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      console.error('Date formatting error:', e);
      return dateString;
    }
  };
  
  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Render the Placeholder Management section
  const renderPlaceholderSection = () => {
    return (
      <div className="admin-section">
        <div className="section-header">
          <h3>Placeholder Management</h3>
        </div>
        <p className="section-description">
          This section allows you to manage placeholder images that can be used in templates.
        </p>
        <div className="placeholder-manager">
          <AdminPlaceholderUpload />
        </div>
      </div>
    );
  };

  // Render the User Management section with mobile-friendly click handlers
  const renderUsersSection = () => {
    return (
      <div className="admin-section">
        <div className="section-header">
          <h3>User Management</h3>
          <div className="header-actions">
            <button
              className="refresh-button"
              onClick={(e) => { e.preventDefault(); fetchUsers(); }}
              title="Refresh Users"
            >
              <i className="fas fa-sync-alt"></i>
            </button>
            <button 
              className="add-button"
              onClick={(e) => {
                e.preventDefault();
                setUserToEdit(null);
                setFormValues({
                  first_name: '',
                  last_name: '',
                  email: '',
                  user_type: 'customer',
                  is_admin: false
                });
                setShowUserForm(true);
              }}
            >
              <i className="fas fa-user-plus"></i> Add User
            </button>
          </div>
        </div>
        
        {mockUsers.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '40px 0' }}>
            <i className="fas fa-users-slash" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }}></i>
            <h4>No Users Found</h4>
            <p>There are no users in the system yet.</p>
            <button 
              className="tool-button" 
              style={{ marginTop: '16px' }}
              onClick={(e) => {
                e.preventDefault();
                setUserToEdit(null);
                setFormValues({
                  first_name: '',
                  last_name: '',
                  email: '',
                  user_type: 'customer',
                  is_admin: false
                });
                setShowUserForm(true);
              }}
            >
              Add First User
            </button>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Admin</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mockUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.first_name} {user.last_name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`user-type ${user.user_type}`}>
                        {capitalizeFirstLetter(user.user_type)}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-status ${user.is_admin ? 'is-admin' : 'not-admin'}`}>
                        {user.is_admin ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="action-button edit"
                          onClick={(e) => handleEditUser(user, e)}
                          title="Edit User"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          className="action-button delete"
                          onClick={(e) => handleDeleteUser(user.id, e)}
                          title="Delete User"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="admin-dashboard-view" ref={containerRef}>
      <h2>Admin Dashboard</h2>
      
      {/* Admin Section Navigation - Updated for better mobile experience */}
      <div className="admin-nav">
        <button 
          className={`nav-button ${activeSection === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleSectionChange('dashboard')}
        >
          <i className="fas fa-tachometer-alt"></i>
          <span className="nav-label">Dashboard</span>
        </button>
        <button 
          className={`nav-button ${activeSection === 'events' ? 'active' : ''}`}
          onClick={() => handleSectionChange('events')}
        >
          <i className="fas fa-calendar-alt"></i>
          <span className="nav-label">Events</span>
        </button>
        <button 
          className={`nav-button ${activeSection === 'users' ? 'active' : ''}`}
          onClick={() => handleSectionChange('users')}
        >
          <i className="fas fa-users"></i>
          <span className="nav-label">Users</span>
        </button>
        <button 
          className={`nav-button ${activeSection === 'placeholders' ? 'active' : ''}`}
          onClick={() => handleSectionChange('placeholders')}
        >
          <i className="fas fa-images"></i>
          <span className="nav-label">Media</span>
        </button>
        <button 
          className={`nav-button ${activeSection === 'dev' ? 'active' : ''}`}
          onClick={() => handleSectionChange('dev')}
        >
          <i className="fas fa-cogs"></i>
          <span className="nav-label">Dev</span>
        </button>
      </div>
      
      {/* Stats Overview - Only show on dashboard view */}
      {activeSection === 'dashboard' && (
        <div className="admin-stats">
          <div className="stat-card">
            <Link to="/dashboard/users" className="stat-link">
              <div className="stat-icon users-icon">
                <i className="fas fa-users"></i>
              </div>
              <div className="stat-info">
                <h3>USERS</h3>
                <p className="stat-value">{stats.users}</p>
              </div>
            </Link>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon events-icon">
              <i className="fas fa-calendar"></i>
            </div>
            <div className="stat-info">
              <h3>EVENTS</h3>
              <p className="stat-value">{stats.events}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon templates-icon">
              <i className="fas fa-file-image"></i>
            </div>
            <div className="stat-info">
              <h3>TEMPLATES</h3>
              <p className="stat-value">{stats.templates}</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon uploads-icon">
              <i className="fas fa-upload"></i>
            </div>
            <div className="stat-info">
              <h3>UPLOADS</h3>
              <p className="stat-value">{stats.uploads}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Admin Tools Section - Only show on dashboard view */}
      {activeSection === 'dashboard' && (
        <div className="admin-section">
          <div className="section-header">
            <h3>Admin Tools</h3>
          </div>
          
          <div className="admin-tools">
            <div className="tool-card">
              <div className="tool-icon">
                <i className="fas fa-users"></i>
              </div>
              <h4>Staff Management</h4>
              <p>Create and manage staff accounts</p>
              <div className="tool-buttons">
                <Link to="/dashboard/staff/new" className="tool-button">Create Staff User</Link>
                <Link to="/dashboard/users" className="tool-button">View All Users</Link>
              </div>
            </div>
            
            <div className="tool-card">
              <div className="tool-icon">
                <i className="fas fa-calendar-plus"></i>
              </div>
              <h4>Event Management</h4>
              <p>Create and manage events</p>
              <button 
                className="tool-button"
                onClick={(e) => { e.preventDefault(); handleSectionChange('events'); }}
              >
                Manage Events
              </button>
            </div>
            
            <div className="tool-card">
              <div className="tool-icon">
                <i className="fas fa-address-book"></i>
              </div>
              <h4>CRM System</h4>
              <p>Manage contacts, teams, and leads</p>
              <Link to="/dashboard/crm" className="tool-button">Open CRM</Link>
            </div>
            
            <div className="tool-card">
              <div className="tool-icon">
                <i className="fas fa-images"></i>
              </div>
              <h4>Template Management</h4>
              <p>Manage and organize templates</p>
              <Link to="/editor" className="tool-button">Create Template</Link>
            </div>
            
            <div className="tool-card">
              <div className="tool-icon">
                <i className="fas fa-cog"></i>
              </div>
              <h4>System Settings</h4>
              <p>Configure system settings</p>
              <div className="tool-buttons">
                <button 
                  className="tool-button"
                  onClick={(e) => { e.preventDefault(); handleSectionChange('placeholders'); }}
                >
                  Placeholder Images
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Conditional rendering based on active section */}
      {activeSection === 'events' && renderEventsSection()}
      {activeSection === 'users' && renderUsersSection()}
      {activeSection === 'placeholders' && renderPlaceholderSection()}
      {activeSection === 'dev' && <DevManagement />}
      
      {/* User Form Modal */}
      {showUserForm && (
        <div className="modal-overlay">
          <div className="user-form-modal">
            <div className="modal-header">
              <h3>{userToEdit ? 'Edit User' : 'Add New User'}</h3>
              <button 
                className="close-button"
                onClick={() => setShowUserForm(false)}
                disabled={loading}
              >
                ×
              </button>
            </div>
            
            {successMessage && (
              <div className="success-message">
                <i className="fa fa-check-circle"></i>
                {successMessage}
              </div>
            )}
            
            <form onSubmit={handleSubmitForm}>
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={formValues.first_name}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={formValues.last_name}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formValues.email}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label>User Type</label>
                <select
                  name="user_type"
                  value={formValues.user_type}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="customer">Customer</option>
                  <option value="staff">Staff</option>
                </select>
              </div>
              
              <div className="form-checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="is_admin"
                    checked={formValues.is_admin}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  Admin Privileges
                </label>
              </div>
              
              <div className="form-buttons">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setShowUserForm(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="loading-text">
                      <span className="spinner-small"></span>
                      {userToEdit ? 'Updating...' : 'Creating...'}
                    </span>
                  ) : (
                    userToEdit ? 'Update User' : 'Create User'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardView; 